import React from 'react';
import { ResponsiveTabs } from '../../../Components/Common/DashboardKit';
import HrEmbeddedPage from './HrEmbeddedPage';

/**
 * COA workspace — Round HR-PA (Part 7).
 *
 * ONE place for everything Change of Attendance, with the owner's sub-nav:
 *
 *   ADD COA | REMOVE COA | APPROVALS | HISTORY
 *
 *   ADD COA   → the existing COA Central HR entry (pre-approved, with the
 *               2-hour policy validation; longer windows need the explicit
 *               HR override + reason and are audited).
 *   REMOVE COA→ the existing Leave Archive locked to COA — find the entry
 *               and HR-void it (audited, reversible history).
 *   APPROVALS → the SAME shared review queue, pre-filtered to COA.
 *   HISTORY   → the archive again, browsing-focused: every COA (pending,
 *               approved, denied) across any date range.
 *
 * A COA is a ≤2-hour schedule change (late arrival / early leave /
 * appointment) — payroll-wise, for SALARIED it fills only the gap it
 * explains; for HOURLY it documents the absence but adds no paid hours.
 * User-facing terminology: "Change of Attendance" (never "Change of
 * Address"); DB/API names (COARequest, /api/coa/…) intentionally
 * unchanged. This file is composition only.
 */
const LazyCoa     = React.lazy(() => import('../CoaCentral'));
const LazyArchive = React.lazy(() => import('../LeaveArchive'));
const LazyQueue   = React.lazy(() => import('../HrReviewDashboard'));

const TABS = [
    { key: 'add',       label: 'Add COA',    icon: 'ri-add-circle-line' },
    { key: 'remove',    label: 'Remove COA', icon: 'ri-delete-bin-6-line' },
    { key: 'approvals', label: 'Approvals',  icon: 'ri-checkbox-multiple-line' },
    { key: 'history',   label: 'History',    icon: 'ri-archive-line' },
];

const HrCoaWorkspace = ({ tab, onTabChange }) => {
    const activeTab = TABS.some((t) => t.key === tab) ? tab : 'add';

    return (
        <div>
            <div className="hr-section-title">
                <div>
                    <h5>Change of Attendance (COA)</h5>
                    <span className="text-muted">
                        Schedule-change records (up to 2 hours for salaried; any window for hourly, documentation only) — add, remove, approvals, history.
                    </span>
                </div>
            </div>

            <ResponsiveTabs
                tabs={TABS}
                activeKey={activeTab}
                onChange={onTabChange}
                variant="pills"
                size="sm"
            />

            {activeTab === 'add' && <HrEmbeddedPage><LazyCoa /></HrEmbeddedPage>}

            {activeTab === 'remove' && (
                <div>
                    <p className="text-muted small mb-2" style={{ maxWidth: 760 }}>
                        Find the COA entry (any status) and void it — mistakes are corrected
                        with a full audit trail, never by editing history.
                    </p>
                    <HrEmbeddedPage><LazyArchive kind="coa" lockKind /></HrEmbeddedPage>
                </div>
            )}

            {activeTab === 'approvals' && (
                <HrEmbeddedPage><LazyQueue initialKind="COA" /></HrEmbeddedPage>
            )}

            {activeTab === 'history' && (
                <HrEmbeddedPage><LazyArchive kind="coa" lockKind /></HrEmbeddedPage>
            )}
        </div>
    );
};

export default HrCoaWorkspace;
